import { cloneDeep } from "lodash"
import {
  Calendar,
  ChevronsRight,
  Circle,
  Clock,
  FileText,
  Globe,
  Mail,
  MapPin,
  Phone,
  Settings,
  Table2,
  Type,
  User,
  Users,
} from "lucide-mui"
import React, { ElementType, useMemo } from "react"

import { ActivityChangeSet } from "@/types/activities"

const CHANGE_KEY_ICONS: Record<string, ElementType> = {
  date: Calendar,
  startTime: Clock,
  endTime: Clock,
  timeRange: Clock,
  tableId: Table2,
  peopleAmount: Users,
  status: Circle,
  firstName: User,
  lastName: User,
  email: Mail,
  phone: Phone,
  name: Type,
  country: Globe,
  timezone: Clock,
  address: MapPin,
  city: MapPin,
  description: FileText,
  facilities: Settings,
  website: Globe,
  zipcode: MapPin,
  venueType: Type,
  socialAccounts: Globe,
  businessName: Type,
}

const getChangeIcon = (key: string): ElementType => {
  return CHANGE_KEY_ICONS[key] || Settings
}

const isChangeSet = (value: any): value is { from: string; to: string } => {
  return value?.from !== undefined && value?.to !== undefined
}

interface ChangeItemProps {
  changeKey: string
  value: any
  label?: string
}

const ChangeItem = ({ changeKey, value, label }: ChangeItemProps) => {
  const Icon = getChangeIcon(changeKey)
  return (
    <div data-testid={`panel-activity-changes-item-${changeKey}`} className="flex items-center gap-1">
      <Icon className="w-3.5 h-3.5 text-gray-400" />
      {label ? <span className="text-gray-600">{label}:</span> : null}
      {isChangeSet(value) ? (
        <>
          <span>{value.from}</span>
          <ChevronsRight className="text-gray-400 w-4 h-4" />
          <span>{value.to}</span>
        </>
      ) : (
        <span>{value as string}</span>
      )}
    </div>
  )
}

interface ChangesProps {
  changes: ActivityChangeSet
  labels: Record<string, string>
  formatters: Record<string, (value: any, key?: string) => any>
  order?: string[]
}

const DEFAULT_ORDER = ["date", "timeRange", "startTime", "endTime", "tableId", "peopleAmount", "status"]

const Changes = ({ changes: changesFromProps, labels, formatters, order = DEFAULT_ORDER }: ChangesProps) => {
  const changes = useMemo(() => {
    const changes = cloneDeep(changesFromProps)

    Object.keys(changes).forEach((key) => {
      const formatter = formatters[key]

      if (!formatter) {
        return
      }

      if (isChangeSet(changes[key])) {
        changes[key].from = formatter(changes[key].from, key)
        changes[key].to = formatter(changes[key].to, key)
      } else {
        changes[key] = formatter(changes[key], key)
      }
    })

    // Combine startTime and endTime into timeRange if both exist
    if (changes.startTime && changes.endTime) {
      const startTime = changes.startTime
      const endTime = changes.endTime

      const startIsChangeSet = isChangeSet(startTime)
      const endIsChangeSet = isChangeSet(endTime)

      if (startIsChangeSet || endIsChangeSet) {
        // At least one is a changeset - create a changeset timeRange
        const startFrom = startIsChangeSet ? startTime.from : startTime
        const startTo = startIsChangeSet ? startTime.to : startTime
        const endFrom = endIsChangeSet ? endTime.from : endTime
        const endTo = endIsChangeSet ? endTime.to : endTime

        changes.timeRange = {
          from: `${startFrom} - ${endFrom}`,
          to: `${startTo} - ${endTo}`,
        }
      } else {
        // Both are simple values: "HH:mm - HH:mm"
        changes.timeRange = `${startTime} - ${endTime}`
      }

      delete changes.startTime
      delete changes.endTime
    }

    const changesArray = Object.entries(changes)

    changesArray.sort(([keyA], [keyB]) => {
      const indexA = order.indexOf(keyA)
      const indexB = order.indexOf(keyB)

      if (indexA === -1 && indexB === -1) {
        return keyA.localeCompare(keyB)
      }

      if (indexA === -1) {
        return 1
      }

      if (indexB === -1) {
        return -1
      }

      return indexA - indexB
    })

    return changesArray
  }, [changesFromProps, formatters])

  return (
    <div data-testid="panel-activity-changes-container" className="w-fit bg-gray-100 rounded-lg px-2 py-1 mt-2">
      <div className="flex flex-row flex-wrap items-center gap-3 text-xs text-gray-700">
        {changes.map(([key, value]) => (
          <ChangeItem key={key} changeKey={key} value={value} label={labels[key]} />
        ))}
      </div>
    </div>
  )
}

export default Changes
